/* =========================================================
   Cart Reminder
   - <cart-reminder-modal> extends the theme's ModalElement
     (show/hide, overlay, focus-trap, escape, aria-controls,
      gesture-close are inherited – not re-implemented).
   - Shows the LAST cart item (image/title/variant/price) live
     from /cart.js; the CTA closes the modal and opens the cart
     drawer. Triggers: desktop exit-intent + optional delay,
     mobile delay + tab-blur. Frequency gate: every trigger /
     once per session / X-day cookie. Console logs only in dev.
   ========================================================= */
(function () {
  function defineCartReminder() {
    if (customElements.get('cart-reminder-modal')) return;
    customElements.define(
      'cart-reminder-modal',
      class CartReminderModal extends ModalElement {
        get shouldLock() {
          return this._centered === true; // only the centered variant darkens + locks scroll
        }
        get shouldAppendToBody() {
          return true;
        }
        get focusElement() {
          return this.cta || this.querySelector('button');
        }

        connectedCallback() {
          super.connectedCallback();
          // Opening moves the element to <body> (shouldAppendToBody) which fires
          // disconnect→connect. On that reconnect, re-attach the cartUpdate subscription
          // (dropped in disconnectedCallback) so the product keeps updating.
          if (this.initialized) { this.ensureCartSubscription(); return; }
          this.initialized = true;

          if (window.location.pathname === '/challenge') return;

          this.config = this.parseConfig();
          if (!this.config) return;
          this.devMode = !!this.config.devMode;
          this._centered = this.config.centered === true;
          if (this.devMode) this.log('Dev-Modus aktiv – Cookie/Session werden ignoriert.');

          this.cookieName = 'DatoraCartReminder';
          this.sessionKey = `DatoraCartReminder:shown:${this.config.version || 'a'}:${this.config.handle || 'x'}`;
          this.cart = null;
          this._shownThisView = false;
          this._timersArmed = false;

          this.cacheElements();
          this.bindCta();
          this.applyDeliveryDays(); // resolve [DAYS] up front (refreshed again on each show)
          this.primeCart();
          this.armTriggers();
        }

        disconnectedCallback() {
          super.disconnectedCallback();
          // Drop the ref (called on every body-append move too); re-subscribed on reconnect.
          if (this._cartUnsub) { this._cartUnsub(); this._cartUnsub = null; }
        }

        /* ---------- Config / DOM ---------- */
        parseConfig() {
          const el = this.querySelector('[data-cart-reminder-config]');
          if (!el) return null;
          try {
            return JSON.parse(el.textContent);
          } catch (err) {
            return null;
          }
        }

        cacheElements() {
          this.productEl = this.querySelector('[data-cart-reminder-product]');
          this.imageEl = this.querySelector('[data-cr-image]');
          this.linkEl = this.querySelector('[data-cr-link]');
          this.titleEl = this.querySelector('[data-cr-title]');
          this.variantEl = this.querySelector('[data-cr-variant]');
          this.priceEl = this.querySelector('[data-cr-price]');
          this.cta = this.querySelector('[data-cr-cta]');
          this.live = this.querySelector('[data-cart-reminder-live]');
          // USP labels that carry the [DAYS] placeholder (keep the original template to allow re-runs)
          this._dayNodes = Array.from(this.querySelectorAll('.cart-reminder__usp-label'))
            .map((label) => ({ label: label, usp: label.closest('.cart-reminder__usp'), tpl: label.textContent }))
            .filter((n) => n.tpl.indexOf('[DAYS]') !== -1);
        }

        /* ---------- Cart data ---------- */
        primeCart() {
          this.fetchCart().then((cart) => {
            if (cart) this.renderProduct();
            this.maybeArmTimers();
          });
          this.ensureCartSubscription();
        }

        // Idempotent: keeps the product fresh + (re)arms timers once the cart is eligible.
        // Re-called on reconnect because the body-append move tears the subscription down.
        ensureCartSubscription() {
          if (this._cartUnsub || !theme || !theme.pubsub) return;
          this._cartUnsub = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (payload) => {
            if (payload && payload.cart) {
              this.cart = payload.cart;
              this.renderProduct();
              this.maybeArmTimers();
            }
          });
        }

        fetchCart() {
          const url = (theme && theme.routes && theme.routes.cart_url) || '/cart';
          return fetch(url + '.js', { headers: { Accept: 'application/json' } })
            .then((res) => (res.ok ? res.json() : null))
            .then((cart) => {
              if (cart) this.cart = cart;
              return cart;
            })
            .catch(() => null);
        }

        lastItem() {
          const items = this.cart && this.cart.items;
          return items && items.length ? items[items.length - 1] : null;
        }

        renderProduct() {
          const item = this.lastItem();
          if (!item) {
            if (this.productEl) this.productEl.hidden = true;
            return;
          }
          if (this.imageEl) {
            const src = (item.featured_image && item.featured_image.url) || item.image || '';
            if (src) {
              this.imageEl.src = this.sizeImage(src, 240);
              this.imageEl.alt = item.product_title || item.title || '';
              if (this.linkEl) this.linkEl.hidden = false;
            } else {
              // no image → drop the src (avoid a broken-image request) and hide the media
              this.imageEl.removeAttribute('src');
              if (this.linkEl) this.linkEl.hidden = true;
            }
          }
          if (this.linkEl && item.url) this.linkEl.href = item.url;
          if (this.titleEl) this.titleEl.textContent = item.product_title || item.title || '';
          if (this.variantEl) {
            const sub = this.buildSubline(item);
            this.variantEl.textContent = sub;
            this.variantEl.hidden = !sub;
          }
          this.buildPrice(item);
          if (this.productEl) this.productEl.hidden = false;
        }

        // Subline mirrors the cart drawer: variant options + visible line-item properties
        // (skip _-prefixed) + selling-plan name (e.g. rental term "Mietdauer 6 Monate").
        buildSubline(item) {
          const parts = [];
          if (item.variant_title && item.variant_title !== 'Default Title') parts.push(item.variant_title);
          const props = item.properties;
          if (props && typeof props === 'object') {
            Object.keys(props).forEach((k) => {
              if (!k || k.charAt(0) === '_') return;
              const v = props[k];
              if (v == null || String(v).trim() === '') return;
              parts.push(k + ': ' + v);
            });
          }
          const sp = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
          if (sp && sp.name) parts.push(sp.name);
          return parts.join(' · ');
        }

        buildPrice(item) {
          if (!this.priceEl) return;
          const fmt = this.moneyFormat();
          const money = (c) => (theme && theme.Currency ? theme.Currency.formatMoney(c, fmt) : c);
          const finalPrice = item.final_price != null ? item.final_price : item.price;
          // A struck "before" price when the line is discounted or a compare-at is present.
          let comparePrice = 0;
          if (item.original_price && item.original_price > finalPrice) comparePrice = item.original_price;
          if (item.compare_at_price && item.compare_at_price > finalPrice) comparePrice = item.compare_at_price;

          const onSale = comparePrice > finalPrice;
          this.priceEl.classList.toggle('price--on-sale', onSale);
          let html = `<span class="price__regular whitespace-nowrap">${money(finalPrice)}</span>`;
          if (onSale) {
            html += `<span class="price__sale inline-flex items-center relative">${money(comparePrice)}</span>`;
          }
          this.priceEl.innerHTML = html;

          // Enrich with the product's compare-at price (not in /cart.js) – non-blocking.
          if (!onSale) this.enrichCompareAt(item, finalPrice);
        }

        enrichCompareAt(item, finalPrice) {
          if (!item.url) return;
          const vid = item.variant_id || item.id;
          const base = item.url.split('?')[0];
          fetch(base + '.js', { headers: { Accept: 'application/json' } })
            .then((res) => (res.ok ? res.json() : null))
            .then((product) => {
              if (!product || !product.variants) return;
              // stale guard: the last cart item may have changed while fetching
              const current = this.lastItem();
              if (!current || String(current.variant_id || current.id) !== String(vid)) return;
              const variant = product.variants.find((v) => String(v.id) === String(vid));
              const compare = variant && variant.compare_at_price;
              if (compare && compare > finalPrice) {
                const money = (c) => (theme && theme.Currency ? theme.Currency.formatMoney(c, this.moneyFormat()) : c);
                this.priceEl.classList.add('price--on-sale');
                this.priceEl.innerHTML =
                  `<span class="price__regular whitespace-nowrap">${money(finalPrice)}</span>` +
                  `<span class="price__sale inline-flex items-center relative">${money(compare)}</span>`;
              }
            })
            .catch(() => {});
        }

        moneyFormat() {
          const s = (theme && theme.settings) || {};
          return s.currencyCodeEnabled ? s.moneyWithCurrencyFormat : s.moneyFormat;
        }

        sizeImage(url, w) {
          if (!url) return '';
          try {
            const u = new URL(url, window.location.origin);
            u.searchParams.set('width', w);
            return u.toString();
          } catch (e) {
            return url;
          }
        }

        /* ---------- Triggers ---------- */
        armTriggers() {
          if (window.Shopify && Shopify.designMode) {
            this.log('Design-Mode: Auto-Trigger deaktiviert – Sektion im Editor auswählen zum Ansehen.');
            return;
          }
          const isDesktop = window.matchMedia('(hover: hover)').matches;
          const isTouch = window.matchMedia('(hover: none)').matches;

          if (isDesktop && this.config.desktopExit) {
            document.addEventListener('mouseout', (e) => {
              if (!e.relatedTarget && !e.toElement && e.clientY <= 0) this.trigger('exit-intent');
            });
          }
          if (isTouch && this.config.mobileTabblur) {
            document.addEventListener('visibilitychange', () => {
              if (document.hidden) this.trigger('tab-blur');
            });
          }
          // delay timers are armed once the cart is eligible (see maybeArmTimers)
          this.maybeArmTimers();
        }

        maybeArmTimers() {
          if (this._timersArmed) return;
          if (window.Shopify && Shopify.designMode) return;
          if (!this.hasItems()) return; // wait until there is something in the cart
          this._timersArmed = true;
          const isDesktop = window.matchMedia('(hover: hover)').matches;
          const isTouch = window.matchMedia('(hover: none)').matches;

          if (isDesktop && this.config.desktopDelayEnabled) {
            const s = Number(this.config.desktopDelay);
            if (!isNaN(s) && s >= 0) setTimeout(() => this.trigger('desktop-delay'), s * 1000);
          }
          if (isTouch && this.config.mobileDelayEnabled) {
            const s = Number(this.config.mobileDelay);
            if (!isNaN(s) && s >= 0) setTimeout(() => this.trigger('mobile-delay'), s * 1000);
          }
        }

        trigger(reason) {
          if (!this.eligible()) return;
          // Cross-feature priority: coordinate with an open Glücksrad overlay.
          const gr = document.querySelector('gluecksrad-drawer');
          if (gr && gr.open) {
            if ((this.config.priority || 'gluecksrad') === 'cart_reminder') {
              // CR wins → close the wheel untouched (it may auto-open again later)
              if (typeof gr.dismissForPriority === 'function') gr.dismissForPriority();
              else if (typeof gr.hide === 'function') gr.hide();
            } else {
              // GR wins → suppress now, but retry once the wheel closes so a single-shot
              // trigger (delay/exit-intent) isn't lost for this pageview.
              this.log('Unterdrückt – Glücksrad gewinnt (Priorität).');
              gr.addEventListener('drawer:afterHide', () => this.trigger(reason), { once: true });
              return;
            }
          }
          this.log('Trigger:', reason);
          this.show();
        }

        hasItems() {
          const min = Math.max(1, Number(this.config.minProducts) || 1);
          const count = this.cart ? this.cart.item_count : 0;
          return count >= min;
        }

        eligible() {
          if (this.open) return false;
          if (!this.hasItems()) return false;
          if (this.devMode) return true;
          if (this._shownThisView) return false; // once per pageview minimum (covers frequency 'days' + expiry 0)
          const freq = this.config.frequency || 'session';
          if (freq === 'always') return !this._shownThisView;
          if (freq === 'session') return !this.getSession(this.sessionKey);
          if (freq === 'days') return !this.getCookie(this.cookieName);
          return true;
        }

        /* ---------- CTA: close + open cart drawer ---------- */
        bindCta() {
          if (!this.cta) return;
          this.cta.addEventListener('click', () => {
            this.log('CTA geklickt – öffne Warenkorb.');
            let done = false;
            const openCart = () => { if (done) return; done = true; this.openCart(); };
            const p = this.hide();
            if (p && typeof p.then === 'function') p.then(openCart);
            // Fallback: open the cart even if the hide-transition promise never resolves
            // (e.g. overlay transition suppressed), so the CTA can't get stuck.
            setTimeout(openCart, 1200);
          });
        }

        // Close because the wheel won the priority – roll back the "shown" state so the
        // reminder is untouched and can trigger again later.
        dismissForPriority() {
          this._shownThisView = false;
          this.clearSession(this.sessionKey);
          this.clearCookie(this.cookieName);
          return this.hide();
        }

        openCart() {
          if (this.config.cartType === 'drawer') {
            const cd = document.querySelector('cart-drawer');
            if (cd && typeof cd.show === 'function') {
              cd.show();
              return;
            }
          }
          window.location.href = (theme && theme.routes && theme.routes.cart_url) || '/cart';
        }

        /* ---------- Delivery-days [DAYS] enrichment ---------- */
        // Resolve the delivery span (days) the same way the cart delivery box computed it:
        // primary = the box's data attributes, fallback = market metafields. Returns "n" | "min-max" | null.
        resolveDeliveryDays() {
          // Delivery days can never be 0 – a 0 / miscalculated / missing box value must fall
          // through to the market-metafield fallback (which guarantees a value so the USP
          // never has to be hidden).
          const box = document.querySelector('cart-drawer .shipping-delivery-window, cart-drawer .shipping-date');
          if (box) {
            const d = parseInt(box.getAttribute('data-delivery-days'), 10);
            if (!isNaN(d) && d > 0) return String(d);
            const mn = parseInt(box.getAttribute('data-delivery-min-days'), 10);
            const mx = parseInt(box.getAttribute('data-delivery-max-days'), 10);
            if (!isNaN(mn) && mn > 0) return this.formatDays(mn, !isNaN(mx) && mx > 0 ? mx : mn);
          }
          // Fallback: market metafields (datora.delivery_min_days / max_days)
          const fmn = parseInt(this.config.deliveryFallbackMin, 10);
          const fmx = parseInt(this.config.deliveryFallbackMax, 10);
          if (!isNaN(fmn) && fmn > 0) return this.formatDays(fmn, !isNaN(fmx) && fmx > 0 ? fmx : fmn);
          return null;
        }

        formatDays(min, max) {
          return !max || max <= min ? String(min) : min + '-' + max;
        }

        applyDeliveryDays() {
          if (!this._dayNodes || !this._dayNodes.length) return;
          if (!this.config.deliveryDays) {
            // feature off → hide [DAYS] USPs so no literal placeholder shows
            this._dayNodes.forEach((n) => { if (n.usp) n.usp.hidden = true; });
            return;
          }
          const str = this.resolveDeliveryDays();
          this._dayNodes.forEach((n) => {
            // Never hide the USP for a missing/0 value – the market fallback provides one.
            // Only if nothing at all resolves (misconfig: no box + no market metafield) drop
            // just the token so no literal "[DAYS]" is shown, but keep the USP visible.
            n.label.textContent = n.tpl.replace(/\[DAYS\]/g, str || '').replace(/\s{2,}/g, ' ').trim();
            if (n.usp) n.usp.hidden = false;
          });
          this.log('Lieferzeit [DAYS]:', str);
        }

        /* ---------- Show / persistence ---------- */
        show(activeElement = null, animate = true) {
          this.renderProduct(); // make sure the latest item is shown
          this.applyDeliveryDays(); // fill [DAYS] from the current cart delivery box
          return super.show(activeElement, animate);
        }

        afterShow() {
          // Guard against a stray afterShow after a priority-dismiss (the show-transition
          // callback can still fire after hide()) → no persistence/lock for a closing modal.
          if (!this.open) return;
          // Centered = modal: darken + scroll-lock + focus-trap (theme default).
          // Edge = non-modal toast: skip the focus-trap so keyboard users aren't
          // captured while the page stays usable.
          if (this._centered) super.afterShow();
          this._shownThisView = true;
          if (this.devMode) return;
          const freq = this.config.frequency || 'session';
          if (freq === 'session') {
            this.setSession(this.sessionKey);
          } else if (freq === 'days') {
            const exp = Number(this.config.expiry);
            if (!isNaN(exp) && exp > 0) this.setCookie(this.cookieName, exp);
          }
          if (this.live) {
            const item = this.lastItem();
            if (item) this.live.textContent = item.product_title || item.title || '';
          }
        }

        /* ---------- Helpers (cookie/session) ---------- */
        getCookie(name) {
          const match = document.cookie.match(`(^|;)\\s*${name.replace(/[-:]/g, '\\$&')}\\s*=\\s*([^;]+)`);
          return match ? match[2] : null;
        }
        setCookie(name, days) {
          document.cookie = `${name}=true; max-age=${days * 24 * 60 * 60}; path=/`;
        }
        clearCookie(name) {
          document.cookie = `${name}=; max-age=0; path=/`;
        }
        getSession(key) {
          try { return sessionStorage.getItem(key); } catch (e) { return null; }
        }
        setSession(key) {
          try { sessionStorage.setItem(key, '1'); } catch (e) {}
        }
        clearSession(key) {
          try { sessionStorage.removeItem(key); } catch (e) {}
        }
        // Console output only in dev mode
        log(...args) { if (this.devMode) console.log('[Cart Reminder]', ...args); }
        warn(...args) { if (this.devMode) console.warn('[Cart Reminder]', ...args); }
      }
    );
  }

  // Define as soon as the theme's ModalElement base class is available (defer load-order safety).
  let _tries = 0;
  (function whenReady() {
    if (typeof ModalElement !== 'undefined') { defineCartReminder(); return; }
    if (_tries++ > 100) { console.warn('[Cart Reminder] ModalElement nicht verfügbar – inaktiv.'); return; }
    setTimeout(whenReady, 50);
  })();
})();
